import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, PlayCircle, BookOpen, Radio, GraduationCap, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { fontClass } from "@/lib/fonts";
import CopyPromptButton from "@/components/CopyPromptButton";
import Seo from "@/components/Seo";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6, ease: "easeOut" } };

const TOOLS = ["ChatGPT", "Gemini", "Claude", "Midjourney", "Nano Banana", "Sora", "Copilot", "NotebookLM", "Perplexity", "ElevenLabs"];

function DateBadge({ date }) {
  const d = new Date(date);
  return (
    <div className="w-14 shrink-0 text-center bg-orange-500/10 border border-orange-400/20 rounded-xl py-2" aria-hidden="true">
      <div className="font-mono font-bold text-lg text-orange-300 leading-none">{d.getDate()}</div>
      <div className="text-[10px] font-mono uppercase tracking-wide text-orange-400 font-bold mt-0.5">
        {d.toLocaleDateString("en-US", { month: "short" })}
      </div>
    </div>
  );
}

function SectionHead({ index, icon: Icon, overline, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="index-num text-2xl">{index}</span>
        <span className="w-8 h-px bg-gradient-to-r from-orange-400/70 to-transparent" />
        <div className="overline-label flex items-center gap-2"><Icon size={13} /> {overline}</div>
      </div>
      <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white leading-[1.1]">
        {children}
      </h2>
    </div>
  );
}

function FeaturedLessonCard({ l }) {
  return (
    <Link
      to={`/lessons/${l.slug}`}
      className="card-void overflow-hidden group relative block h-full min-h-[380px] md:min-h-[460px]"
      data-testid={`lesson-card-${l.slug}`}
    >
      {l.cover_image && (
        <img
          src={l.cover_image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-90 group-hover:scale-[1.03] transition-[opacity,transform] duration-700"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#02080A] via-[#02080A]/60 to-transparent" />
      {l.video_url && (
        <div className="absolute top-5 right-5">
          <span className="w-12 h-12 rounded-full bg-white/95 shadow-lg flex items-center justify-center">
            <PlayCircle className="text-orange-400" size={26} />
          </span>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 p-7 md:p-9">
        <div className="flex items-center gap-3 mb-4">
          <span className="chip">{l.category}</span>
          <span className="meta-mono">
            {new Date(l.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        </div>
        <h3 className={`${fontClass(l.title_font, "font-bn")} font-bold text-2xl md:text-[34px] text-white leading-snug group-hover:text-orange-200 transition-colors max-w-2xl`}>
          {l.title}
        </h3>
        <p className="mt-3 text-[15px] md:text-base text-slate-200 font-bn leading-relaxed line-clamp-2 max-w-xl">{l.excerpt}</p>
        <div className="mt-6 text-orange-400 text-xs font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
          Read Lesson <ArrowRight size={13} />
        </div>
      </div>
    </Link>
  );
}

function LessonCard({ l }) {
  return (
    <Link to={`/lessons/${l.slug}`} className="card-void overflow-hidden block group h-full flex flex-col" data-testid={`lesson-card-${l.slug}`}>
      <div className="aspect-[16/9] overflow-hidden relative bg-white/10">
        {l.cover_image ? (
          <img src={l.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="text-slate-400" size={36} />
          </div>
        )}
        {l.video_url && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/25 group-hover:bg-slate-900/35 transition-colors">
            <span className="w-14 h-14 rounded-full bg-white/95 shadow-lg flex items-center justify-center">
              <PlayCircle className="text-orange-400" size={30} />
            </span>
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="chip">{l.category}</span>
          <span className="meta-mono">
            {new Date(l.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        </div>
        <h3 className={`${fontClass(l.title_font, "font-bn")} font-bold text-lg text-white leading-snug group-hover:text-orange-300 transition-colors`}>
          {l.title}
        </h3>
        <p className="mt-2.5 text-[15px] text-slate-200 font-bn leading-relaxed line-clamp-2 flex-1">{l.excerpt}</p>
        <div className="mt-4 pt-4 border-t border-white/[0.07] text-orange-400 text-xs font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
          Read Lesson <ArrowRight size={13} />
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [lessons, setLessons] = useState([]);
  const [news, setNews] = useState([]);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    api.get("/lessons").then(r => setLessons(r.data)).catch(() => {});
    api.get("/news", { params: { limit: 6 } }).then(r => setNews(r.data)).catch(() => {});
    api.get("/prompts").then(r => setPrompts(r.data)).catch(() => {});
  }, []);

  const stats = [
    { k: `${lessons.length || "—"}`, label: "Free Lessons", bn: "Step-by-step" },
    { k: `${news.length || "—"}+`, label: "AI Updates Tracked", bn: "Daily signal" },
    { k: `${prompts.length || "—"}`, label: "Ready Prompts", bn: "Copy & run" },
    { k: "100%", label: "Free Content", bn: "No paywall" },
  ];

  return (
    <div className="overflow-x-hidden">
      <Seo title={null} description="PromptsHouseBD — free step-by-step AI lessons, a ready-to-use prompt library, and daily AI news that matters." path="/" />
      {/* ── HERO — centered, orbs behind ── */}
      <section className="relative pt-40 pb-14 md:pt-52 md:pb-16 overflow-hidden" data-testid="hero-section">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-orange-300 rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.18em] mb-9"
          >
            <GraduationCap size={13} /> Educational Hub · Learn AI Step by Step
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-bold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-[88px] text-white"
            style={{ textShadow: "0 4px 40px rgba(0,0,0,0.6)" }}
          >
            Master AI,
            <br />
            <span className="text-gradient-glow">step by step.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-7 font-bn-display italic text-xl md:text-2xl text-slate-100"
          >
            Simple lessons, daily AI updates — everything in one place.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-5 text-slate-100 text-base md:text-lg max-w-xl mx-auto leading-relaxed"
          >
            Step-by-step lessons with images and videos, every important AI update explained, and a ready-to-use prompt library — built for ambitious learners.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-11 flex flex-wrap items-center justify-center gap-4"
          >
            <Link to="/lessons" className="btn-glow" data-testid="hero-cta">
              <BookOpen size={15} /> Start Learning
            </Link>
            <Link to="/news" className="btn-glass" data-testid="hero-news-cta">
              <Radio size={14} /> Latest Updates
            </Link>
          </motion.div>
        </div>

        {/* Tools marquee — the world we cover */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="relative mt-20 marquee-mask overflow-hidden"
          data-testid="tools-marquee"
        >
          <div className="marquee-track flex items-center">
            {[...TOOLS, ...TOOLS].map((tool, i) => (
              <span key={`${tool}-${i}`} className="flex items-center shrink-0">
                <span className="meta-mono !text-[13px] !text-slate-300 px-6">{tool}</span>
                <span className="text-orange-400/60 text-[10px]">✦</span>
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── STATS ── */}
      <section className="border-y border-white/10 bg-[#04121A]/40 backdrop-blur-sm" data-testid="hero-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4">
          {stats.map((s, i) => (
            <div key={s.label} className={`py-10 px-6 ${i !== 0 ? "md:border-l md:border-white/10" : ""} ${i % 2 !== 0 ? "border-l border-white/10 md:border-l" : ""}`}>
              <div className="meta-mono !text-orange-300/80 mb-2">0{i + 1}</div>
              <div className="font-display font-bold text-4xl md:text-5xl text-white">{s.k}</div>
              <div className="text-sm text-slate-200 mt-1.5">{s.label}</div>
              <div className="font-bn text-xs text-slate-200">{s.bn}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── LATEST UPDATES ── */}
      <section className="py-20 md:py-32" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
            <SectionHead index="01" icon={Radio} overline="Latest Updates">
              Every AI story, <span className="text-gradient">in one place.</span>
            </SectionHead>
            <Link to="/news" className="btn-glass shrink-0" data-testid="news-see-all">
              All Updates <ArrowUpRight size={14} />
            </Link>
          </motion.div>

          <motion.div {...fadeUp} className="bg-[#0A1115]/80 backdrop-blur-xl border border-white/10 rounded-2xl divide-y divide-white/[0.07] overflow-hidden edge-glow">
            {news.map((n) => {
              const isArticle = n.is_article && n.slug;
              const Row = isArticle ? Link : "a";
              const rowProps = isArticle ? { to: `/news/${n.slug}` } : { href: n.url, target: "_blank", rel: "noreferrer" };
              return (
              <Row
                key={n.id}
                {...rowProps}
                className="flex items-center gap-4 md:gap-6 p-4 md:p-5 hover:bg-white/5 transition-colors group"
                data-testid={`home-news-row-${n.id}`}
              >
                <DateBadge date={n.published_at} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <span className="chip">{n.category}</span>
                    {n.source && <span className="meta-mono">{n.source}</span>}
                  </div>
                  <div className={`${fontClass(n.title_font)} font-semibold text-white text-base md:text-lg leading-snug truncate group-hover:text-orange-300 transition-colors`}>
                    {n.title}
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-slate-400 group-hover:text-orange-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[color,transform] shrink-0" />
              </Row>
              );
            })}
            {news.length === 0 && <div className="p-10 text-center text-slate-200">No updates yet.</div>}
          </motion.div>
        </div>
      </section>

      {/* ── LESSONS — bento grid ── */}
      <section className="py-20 md:py-32 border-y border-white/10" data-testid="lessons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
            <div>
              <SectionHead index="02" icon={BookOpen} overline="Free Lessons">
                Learn step by step, <span className="text-gradient">the easy way.</span>
              </SectionHead>
              <p className="mt-4 text-slate-200 max-w-xl">
                Every lesson comes with images, videos, and hands-on practice — built for beginners.
              </p>
            </div>
            <Link to="/lessons" className="btn-glass shrink-0" data-testid="lessons-see-all">
              All Lessons <ArrowUpRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {lessons[0] && (
              <motion.div {...fadeUp} className="md:col-span-7">
                <FeaturedLessonCard l={lessons[0]} />
              </motion.div>
            )}
            {lessons[1] && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.1 }}
                className="md:col-span-5"
              >
                <LessonCard l={lessons[1]} />
              </motion.div>
            )}
            {lessons.slice(2, 5).map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="md:col-span-4"
              >
                <LessonCard l={l} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROMPTS STRIP ── */}
      <section className="py-20 md:py-32" data-testid="prompts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
            <SectionHead index="03" icon={MessageSquare} overline="Prompt Library">
              Prompts that <span className="text-gradient">actually work.</span>
            </SectionHead>
            <Link to="/prompts" className="btn-glass shrink-0" data-testid="prompts-see-all">
              Prompt Library <ArrowUpRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {prompts.slice(0, 3).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
              >
                <Link to={`/prompts/${p.slug}`} className="card-void overflow-hidden block group h-full flex flex-col" data-testid={`home-prompt-${p.slug}`}>
                  <div className="aspect-[16/9] overflow-hidden bg-white/10">
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <span className="chip self-start mb-3">{p.category}</span>
                    <h3 className="font-display font-bold text-lg text-white leading-snug group-hover:text-orange-300 transition-colors">{p.title}</h3>
                    <p className="mt-2 text-[15px] text-slate-200 line-clamp-2 flex-1">{p.summary}</p>
                    <div className="mt-auto pt-4 border-t border-white/[0.07] flex items-center justify-between">
                      <span className="text-orange-400 text-xs font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                        Open <ArrowUpRight size={12} />
                      </span>
                      <CopyPromptButton text={p.prompt_text} testId={`copy-prompt-home-${p.slug}`} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
